import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IHttpClient } from '@/shared/contracts/http-client.contract';
import { HttpMethod } from '@/shared/contracts/http-client.contract';

import { UIError } from '@/shared/utils/ui-error';

import {
  ENDPOINT,
  RATING_MIN,
  RATING_MAX,
  COMMENT_MAX_LENGTH,
  type RateOrderBody,
  type RateOrderRequest,
  type RateOrderResponse,
} from '../types/rate-order';

type UseCaseInput = RateOrderRequest;
type UseCaseOutput = RateOrderResponse;

/**
 * Registra a avaliação de um pedido finalizado.
 *
 * Usado no fluxo de avaliação do pedido: quando um pedido está apto a ser avaliado
 * (is_ratable === true), o cliente pode atribuir uma nota de 1 a 5 estrelas e, opcionalmente,
 * deixar um comentário. O backend persiste a avaliação e retorna o registro criado, permitindo
 * que a UI atualize o cache e deixe de exibir o convite para avaliar.
 */
export class RateOrderUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { order_id, rating, comment, source } = input;

    if (!order_id) {
      throw new UIError('ORDER_NOT_FOUND', 'Pedido não encontrado.');
    }

    if (!Number.isInteger(rating) || rating < RATING_MIN || rating > RATING_MAX) {
      throw new UIError(
        'INVALID_RATING',
        `A nota deve ser um número inteiro entre ${RATING_MIN} e ${RATING_MAX}.`
      );
    }

    const trimmedComment = comment?.trim();

    if (trimmedComment && trimmedComment.length > COMMENT_MAX_LENGTH) {
      throw new UIError(
        'COMMENT_TOO_LONG',
        `O comentário deve ter no máximo ${COMMENT_MAX_LENGTH} caracteres.`
      );
    }

    const body: RateOrderBody = {
      rating,
      // Envia o comentário apenas quando houver conteúdo após o trim.
      ...(trimmedComment ? { comment: trimmedComment } : {}),
      ...(source ? { source } : {}),
    };

    return this.client.sendRequest<UseCaseOutput, RateOrderBody>({
      endpoint: ENDPOINT(order_id),
      method: HttpMethod.POST,
      body,
    });
  }
}
